import { Box, Flex, Stack, Text } from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router';
import type { CheckoutSuccess, Product } from '../useCheckoutForm';
import { formatAmount, productTitle } from './format';
import { Eyebrow } from './Section';

interface ConfirmationProps {
  product: Product;
  result: CheckoutSuccess;
  amountPaid: number;
  isContract: boolean;
}

function paymentLabel(result: CheckoutSuccess): string {
  if (result.kind === 'comp') return 'Code promo / carte cadeau';
  if (result.paymentMethod === 'Apple Pay') return 'Apple Pay';
  if (result.paymentMethod === 'Alma') return 'Alma';
  return 'Carte bancaire';
}

/** Étape 3 « Confirmation », rendue dans la page à la place du formulaire. */
export function Confirmation({ product, result, amountPaid, isContract }: ConfirmationProps) {
  const navigate = useNavigate();
  const isGiftCard = product.type === 'giftcard';

  const title = isContract ? 'Abonnement activé' : isGiftCard ? 'Carte cadeau envoyée' : 'Paiement confirmé';
  const message = isContract
    ? "Votre abonnement peut apparaître dans Mon compte d'ici une minute."
    : isGiftCard
      ? 'Le bénéficiaire recevra sa carte cadeau par e-mail dans quelques instants.'
      : "Votre crédit a été ajouté à votre compte. Il peut mettre quelques instants à s'afficher.";

  const Row = ({ label, value }: { label: string; value: string }) => (
    <Flex justify="space-between" gap={4} align="baseline">
      <Text className="cv2-body">{label}</Text>
      <Text className="cv2-strong" fontSize="14px" textAlign="right">
        {value}
      </Text>
    </Flex>
  );

  return (
    <Box className="CheckoutV2Confirmation" maxW="560px" mx="auto" w="full">
      <Stack className="cv2-section" spacing={6} align="center" textAlign="center" py={{ base: 8, md: 10 }}>
        <Box className="cv2-confirm-badge">
          <Box className="cv2-confirm-badge-inner">
            <CheckIcon boxSize="22px" />
          </Box>
        </Box>

        <Stack spacing={2} align="center">
          <Text as="h2" className="cv2-confirm-title">
            {title}
          </Text>
          <Text className="cv2-body" maxW="420px">
            {message}
          </Text>
        </Stack>

        <Box className="cv2-confirm-recap">
          <Eyebrow>Récapitulatif</Eyebrow>
          <Row label={isContract ? 'Abonnement' : 'Offre'} value={productTitle(product)} />
          <Row label="Montant réglé" value={formatAmount(amountPaid)} />
          <Row label="Moyen de paiement" value={paymentLabel(result)} />
        </Box>

        <Stack w="full" spacing={3}>
          {!isGiftCard && (
            <button type="button" className="cv2-cta cv2-cta--full" onClick={() => navigate('/', { replace: true })}>
              Réserver un cours
            </button>
          )}
          <button type="button" className="cv2-btn-secondary" onClick={() => navigate(isContract ? '/account#mon-abonnement' : isGiftCard ? '/tarifs' : '/account/services', { replace: true })}>
            {isContract ? 'Voir mon abonnement' : isGiftCard ? 'Retour aux tarifs' : 'Voir mes crédits'}
          </button>
        </Stack>

        <Text className="cv2-small">Un e-mail de confirmation vous a été envoyé.</Text>
      </Stack>
    </Box>
  );
}
